import React, { useState, useEffect } from 'react';
import CountryFlag from '../../../shared/components/CountryFlag';
import {
    Card,
    Table,
    Empty,
    Button,
    Space,
    Tag,
    Modal,
    Form,
    Input,
    Select,
    message,
    Tooltip,
    InputNumber,
    Badge,
    Tabs,
    Row,
    Col,
    Collapse,
    Dropdown,
    Typography,
    Alert,
    Popconfirm
} from 'antd';
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    ReloadOutlined,
    BranchesOutlined,
    EyeOutlined,
    SyncOutlined,
    WarningOutlined
} from '@ant-design/icons';
import cdnService from '../../services/cdnService';
import dnsService from '../../services/dnsService';
import cdnDnsOpsService, { describeSyncResult } from '../../services/cdnDnsOpsService';
import cdnNodeOpsService from '../../services/cdnNodeOpsService';
import { useTranslation } from '../../../shared/i18n';
import DnsSyncPlanView from './DnsSyncPlanView';

const { Option } = Select;
const { TextArea } = Input;
const { Text } = Typography;

const nodeIdsOf = (r) => (r?.node_id ? String(r.node_id).split(',').map(x => parseInt(x.trim(), 10)).filter(Boolean) : []);

const isDefaultLine = (r) => ['continent', 'country', 'province', 'city', 'isp'].every(f => !r[f] || r[f] === '0') && !(r.cidr_id > 0);

// 线路显示名：有名称用名称；旧线路按条件生成
const lineLabel = (r) => {
    if (r.name) return r.name;
    if (isDefaultLine(r)) return '默认';
    const parts = [r.cidr_name, r.continent_name, r.country_name, r.province_name, r.city_name, r.isp_name].filter(Boolean);
    return parts.length ? parts.join('/') : `线路 #${r.id}`;
};

const CdnLineRecords = () => {
    const [records, setRecords] = useState([]);
    const [groups, setGroups] = useState([]);
    const [nodes, setNodes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingRecord, setEditingRecord] = useState(null);
    const [selectedGroupId, setSelectedGroupId] = useState(null);
    const [plan, setPlan] = useState(null);
    const [previewing, setPreviewing] = useState(false);
    const [syncLoading, setSyncLoading] = useState(false);
    const [unassignedNodes, setUnassignedNodes] = useState([]);
    const [activeLineKey, setActiveLineKey] = useState(null);
    const [copySource, setCopySource] = useState(null);
    const [ipPools, setIpPools] = useState([]);
    const { t } = useTranslation();
    const [saving, setSaving] = useState(false);
    const [form] = Form.useForm();

    // IP 子池:弹窗打开时读该线路已选的子池(复制新建沿用来源线路的)
    useEffect(() => {
        if (!modalVisible) return;
        const lid = editingRecord?.id || copySource?.id || 0;
        cdnNodeOpsService.getLinePools(lid).then((r) => {
            if (!r.success) return;
            setIpPools(r.data?.pools || []);
            form.setFieldsValue({ pool_ids: r.data?.pool_ids || [] });
        });
    }, [modalVisible, editingRecord, copySource, form]);

    // 地理位置和ISP、CIDR选项（所有地理位置都是动态加载）
    const [continents, setContinents] = useState([]);
    const [countries, setCountries] = useState([]); // 动态加载
    const [regions, setRegions] = useState([]); // 动态加载
    const [cities, setCities] = useState([]); // 动态加载
    const [ispCountries, setIspCountries] = useState([]);
    const [ispTypes, setIspTypes] = useState([]); // 动态加载
    const [isps, setIsps] = useState([]); // 动态加载
    const [cidrs, setCidrs] = useState([]);

    useEffect(() => {
        loadGroups();
        loadNodes();
        loadGeoOptions();
        loadIspOptions();
        loadCidrOptions();
    }, []);

    useEffect(() => {
        if (selectedGroupId) {
            loadRecords();
        }
    }, [selectedGroupId]);

    const loadGroups = async () => {
        try {
            const response = await cdnService.getCdnGroups(null, 'active');
            if (response.success) {
                const groupData = response.data || [];
                setGroups(groupData);
                if (groupData.length > 0 && !selectedGroupId) {
                    setSelectedGroupId(groupData[0].id);
                }
            }
        } catch (error) {
            message.error('加载分组失败: ' + error.message);
        }
    };

    const loadRecords = async () => {
        setLoading(true);
        try {
            const response = await cdnService.getLineRecords(selectedGroupId, null, null, 100);
            if (response.success) {
                setRecords(response.data?.items || []);
                setUnassignedNodes(response.data?.unassigned_active_nodes || []);
            } else {
                message.error(response.error || '加载线路记录失败');
            }
        } catch (error) {
            message.error('加载线路记录失败: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const loadNodes = async () => {
        try {
            const response = await cdnService.getNodes();
            if (response.success) {
                setNodes(response.data || []);
            }
        } catch (error) {
            console.error('加载节点失败:', error);
        }
    };

    const loadGeoOptions = async () => {
        try {
            // 只加载大洲列表，国家/省份/城市在选择后动态加载
            const continentsRes = await dnsService.getContinents();
            if (continentsRes.success) {
                setContinents(continentsRes.data || []);
            }
        } catch (error) {
            console.error('加载地理位置选项失败:', error);
        }
    };

    const loadIspOptions = async () => {
        try {
            // 只加载ISP国家列表，类型和ISP列表在选择国家后动态加载
            const countriesRes = await dnsService.getIspCountries();
            if (countriesRes.success) {
                setIspCountries(countriesRes.data || []);
            }
        } catch (error) {
            console.error('加载ISP选项失败:', error);
        }
    };

    const loadCidrOptions = async () => {
        try {
            const response = await dnsService.getCidrs();
            if (response.success) {
                setCidrs(response.data || []);
            }
        } catch (error) {
            console.error('加载CIDR选项失败:', error);
        }
    };

    // 处理大洲变化 - 动态加载国家
    const handleContinentChange = async (value) => {
        form.setFieldsValue({ continent: value, country: '0', province: '0', city: '0' });

        if (!value || value === '0') {
            // 清空国家/省份/城市列表
            setCountries([]);
            setRegions([]);
            setCities([]);
        } else {
            try {
                // 动态加载该大洲的国家
                const countriesRes = await dnsService.getCountriesByContinent(value);
                if (countriesRes.success) {
                    setCountries(countriesRes.data || []);
                }
                // 清空省份和城市
                setRegions([]);
                setCities([]);
            } catch (error) {
                console.error('加载国家列表失败:', error);
                message.error('加载国家列表失败');
            }
        }
    };

    // 处理国家变化 - 动态加载省份，并同步ISP国家
    const handleCountryChange = async (value) => {
        form.setFieldsValue({ country: value, province: '0', city: '0' });

        if (!value || value === '0') {
            // 清空省份和城市列表
            setRegions([]);
            setCities([]);
            // 清空ISP国家同步
            form.setFieldsValue({ isp_country: '', isp_type: '', isp: '0' });
            setIspTypes([]);
            setIsps([]);
        } else {
            try {
                // 动态加载该国家的省份
                const regionsRes = await dnsService.getRegions(value);
                if (regionsRes.success) {
                    setRegions(regionsRes.data || []);
                }
                // 清空城市
                setCities([]);

                // 同步ISP国家：查找对应的ISP国家代码
                const countryData = countries.find(c => c.id.toString() === value);
                if (countryData && countryData.country_code) {
                    const ispCountryCode = countryData.country_code;
                    // 检查ISP国家列表中是否存在该国家
                    const ispCountryExists = ispCountries.find(c => c.country === ispCountryCode);
                    if (ispCountryExists) {
                        form.setFieldsValue({ isp_country: ispCountryCode, isp_type: '', isp: '0' });
                        // 加载该国家的ISP类型和ISP列表
                        try {
                            const typesRes = await dnsService.getIspTypes(ispCountryCode);
                            if (typesRes.success) {
                                setIspTypes(typesRes.data || []);
                            }
                            const ispsRes = await dnsService.getIsps(ispCountryCode);
                            if (ispsRes.success) {
                                setIsps(ispsRes.data || []);
                            }
                        } catch (err) {
                            console.error('加载ISP选项失败:', err);
                        }
                    } else {
                        // 该国家没有ISP数据，清空ISP选项
                        form.setFieldsValue({ isp_country: '', isp_type: '', isp: '0' });
                        setIspTypes([]);
                        setIsps([]);
                    }
                }
            } catch (error) {
                console.error('加载省份列表失败:', error);
                message.error('加载省份列表失败');
            }
        }
    };

    // 处理省份变化 - 动态加载城市
    const handleProvinceChange = async (value) => {
        form.setFieldsValue({ province: value, city: '0' });

        const countryCode = form.getFieldValue('country');

        if (!value || value === '0') {
            // 清空城市列表
            setCities([]);
        } else {
            if (!countryCode || countryCode === '0') {
                message.warning('请先选择国家');
                return;
            }

            try {
                // 动态加载该省份的城市
                const citiesRes = await dnsService.getCities(countryCode, value);
                if (citiesRes.success) {
                    setCities(citiesRes.data || []);
                }
            } catch (error) {
                console.error('加载城市列表失败:', error);
                message.error('加载城市列表失败');
            }
        }
    };

    // 处理ISP国家变化 - 动态加载ISP类型和ISP
    const handleIspCountryChange = async (value) => {
        form.setFieldsValue({ isp_country: value, isp_type: '', isp: '0' });

        if (!value) {
            // 清空ISP类型和ISP列表
            setIspTypes([]);
            setIsps([]);
        } else {
            try {
                // 动态加载该国家的ISP类型
                const typesRes = await dnsService.getIspTypes(value);
                if (typesRes.success) {
                    setIspTypes(typesRes.data || []);
                }

                // 动态加载该国家的ISP列表
                const ispsRes = await dnsService.getIsps(value);
                if (ispsRes.success) {
                    setIsps(ispsRes.data || []);
                }
            } catch (error) {
                console.error('加载ISP选项失败:', error);
                message.error('加载ISP选项失败');
            }
        }
    };

    // 处理ISP类型变化 - 动态加载ISP
    const handleIspTypeChange = async (value) => {
        form.setFieldsValue({ isp_type: value, isp: '0' });

        const ispCountry = form.getFieldValue('isp_country');

        if (!ispCountry) {
            message.warning('请先选择ISP国家');
            return;
        }

        try {
            // 动态加载该国家+类型的ISP列表
            const ispsRes = await dnsService.getIsps(ispCountry, value);
            if (ispsRes.success) {
                setIsps(ispsRes.data || []);
            }
        } catch (error) {
            console.error('加载ISP列表失败:', error);
            message.error('加载ISP列表失败');
        }
    };

    const resetCascades = () => {
        setCountries([]);
        setRegions([]);
        setCities([]);
        setIspTypes([]);
        setIsps([]);
    };

    // 空白新建（分组还没有任何线路时）
    const handleAdd = () => {
        if (!selectedGroupId) {
            message.warning('请先选择一个CDN分组');
            return;
        }
        setEditingRecord(null);
        setCopySource(null);
        resetCascades();
        form.resetFields();
        form.setFieldsValue({
            group_id: selectedGroupId,
            name: records.length ? '' : '默认',
            ttl: 120,
            priority: 0,
            weight: 100,
            continent: '0',
            country: '0',
            province: '0',
            city: '0',
            isp: '0',
            isp_country: '',
            isp_type: '',
            cidr_id: 0,
            status: 'active'
        });
        setModalVisible(true);
    };

    // 把一条线路的配置填进表单（编辑 / 复制新建共用），并加载级联选项以显示已选值
    const fillFormFrom = async (record, name) => {
        let loadedCountries = [];
        let ispCountryCode = record.isp_country || '';
        resetCascades();

        if (record.continent && record.continent !== '0') {
            try {
                const countriesRes = await dnsService.getCountriesByContinent(record.continent);
                if (countriesRes.success) {
                    loadedCountries = countriesRes.data || [];
                    setCountries(loadedCountries);
                }
            } catch (error) {
                console.error('加载国家列表失败:', error);
            }
        }

        if (record.country && record.country !== '0') {
            try {
                const regionsRes = await dnsService.getRegions(record.country);
                if (regionsRes.success) {
                    setRegions(regionsRes.data || []);
                }
                const countryData = loadedCountries.find(c => c.id.toString() === record.country);
                if (countryData && countryData.country_code) {
                    ispCountryCode = countryData.country_code;
                }
            } catch (error) {
                console.error('加载省份列表失败:', error);
            }
        }

        if (record.province && record.province !== '0' && record.country && record.country !== '0') {
            try {
                const citiesRes = await dnsService.getCities(record.country, record.province);
                if (citiesRes.success) {
                    setCities(citiesRes.data || []);
                }
            } catch (error) {
                console.error('加载城市列表失败:', error);
            }
        }

        if (ispCountryCode) {
            try {
                const typesRes = await dnsService.getIspTypes(ispCountryCode);
                if (typesRes.success) {
                    setIspTypes(typesRes.data || []);
                }
                const ispsRes = await dnsService.getIsps(ispCountryCode, record.isp_type);
                if (ispsRes.success) {
                    setIsps(ispsRes.data || []);
                }
            } catch (error) {
                console.error('加载ISP选项失败:', error);
            }
        }

        form.resetFields();
        form.setFieldsValue({
            group_id: record.group_id,
            name,
            node_ids: nodeIdsOf(record),
            ttl: record.ttl,
            priority: record.priority,
            weight: record.weight,
            continent: record.continent || '0',
            country: record.country || '0',
            province: record.province || '0',
            city: record.city || '0',
            isp: record.isp || '0',
            isp_country: ispCountryCode,
            isp_type: record.isp_type || '',
            cidr_id: record.cidr_id || 0,
            status: record.status,
            comment: record.comment
        });
    };

    const handleEdit = async (record) => {
        setEditingRecord(record);
        setCopySource(null);
        await fillFormFrom(record, record.name || '');
        setModalVisible(true);
    };

    // 新建线路 = 复制某条线路的全部配置，管理员再改
    const handleCopy = async (source) => {
        if (!source) {
            handleAdd();
            return;
        }
        setEditingRecord(null);
        setCopySource(source);
        await fillFormFrom(source, '');
        setModalVisible(true);
    };

    const showSyncResult = (r) => {
        if (!r) return;
        const d = describeSyncResult(r);
        message[d.type]('DNS：' + d.text, 5);
    };

    const handleDelete = (record) => {
        Modal.confirm({
            title: `删除线路「${lineLabel(record)}」？`,
            okText: '删除',
            cancelText: '取消',
            okType: 'danger',
            onOk: async () => {
                const response = await cdnService.deleteLineRecord(record.id);
                if (response.success) {
                    message.success('已删除');
                    showSyncResult(response.dns_sync);
                    setActiveLineKey(null);
                    loadRecords();
                } else {
                    message.error(response.error || '删除失败');
                }
            }
        });
    };

    const handleSubmit = async () => {
        let values;
        try {
            values = await form.validateFields();
        } catch (error) {
            message.error('请填写所有必填项');
            return;
        }
        const nodeIds = values.node_ids || [];
        if (nodeIds.length === 0) {
            message.error('请至少选择一个节点');
            return;
        }
        if (values.isp && values.isp !== '0' && !values.isp_country) {
            message.error('选择ISP时必须先选择国家');
            return;
        }
        if (values.isp_type && !values.isp_country) {
            message.error('选择ISP类型时必须先选择国家');
            return;
        }

        const submitData = { ...values, name: (values.name || '').trim(), node_id: nodeIds.join(',') };
        delete submitData.node_ids;
        const poolIds = values.pool_ids || [];
        delete submitData.pool_ids;
        if (!submitData.isp) submitData.isp = '0';
        if (submitData.isp === '0' && !submitData.isp_type) submitData.isp_country = '';

        setSaving(true);
        let response;
        try {
            if (editingRecord) {
                response = await cdnService.updateLineRecord(editingRecord.id, submitData);
            } else if (copySource) {
                delete submitData.group_id;
                response = await cdnService.copyLineRecord(copySource.id, submitData);
            } else {
                response = await cdnService.createLineRecord(submitData);
            }
        } finally {
            setSaving(false);
        }

        if (response.success) {
            const savedId = response.data?.id || editingRecord?.id;
            if (savedId) {
                const pr = await cdnNodeOpsService.saveLinePools(savedId, poolIds);
                if (!pr.success) message.error(pr.error || t('Save failed'));
            }
            message.success('已保存');
            showSyncResult(response.dns_sync);
            setModalVisible(false);
            const newId = response.data?.id || editingRecord?.id;
            if (newId) setActiveLineKey(String(newId));
            loadRecords();
        } else {
            message.error(response.error || '保存失败');
        }
    };

    // 预览：与调度器同一个 planGroup（只读，不写 DNS）
    const handlePreview = async () => {
        if (!selectedGroupId) return;
        setPreviewing(true);
        const response = await cdnDnsOpsService.previewGroup(selectedGroupId);
        setPreviewing(false);
        if (response.success) {
            setPlan(response);
        } else {
            message.error(response.error || '预览失败');
        }
    };

    const handleSyncDns = async () => {
        if (!selectedGroupId) return;
        setSyncLoading(true);
        const response = await cdnDnsOpsService.syncGroup(selectedGroupId);
        setSyncLoading(false);
        const d = describeSyncResult(response);
        message[d.type](d.text, 6);
        setPlan(null);
    };

    // ---- 线路 Tab ----
    const lines = [...records].sort((a, b) => (a.priority - b.priority) || (a.id - b.id));
    const defaultLine = lines.find(isDefaultLine) || null;
    const labelCounts = {};
    lines.forEach(l => { const k = lineLabel(l); labelCounts[k] = (labelCounts[k] || 0) + 1; });
    const tabLabel = (l) => {
        const base = lineLabel(l);
        return !l.name && labelCounts[base] > 1 ? `${base} #${l.id}` : base;
    };
    const activeLine = lines.find(l => String(l.id) === activeLineKey) || lines[0] || null;

    const nodeById = {};
    nodes.forEach(n => { nodeById[n.id] = n; });
    // 不少节点的 hostname 就是 IP,显示名称 + 位置代码更好认
    const nodeLabel = (n) => `${n.name || n.hostname}${n.colo ? ` · ${n.colo}` : ''}`;

    const nodeColumns = [
        {
            title: '节点',
            key: 'hostname',
            render: (_, n) => n.missing
                ? <Text type="danger"><WarningOutlined /> 死节点引用 #{n.id}</Text>
                : <Text strong>{nodeLabel(n)}</Text>
        },
        { title: 'IP', key: 'ip', render: (_, n) => <Text type="secondary">{[n.ipv4 || n.ip, n.ipv6].filter(Boolean).join(' / ') || '-'}</Text> },
        {
            title: '状态',
            key: 'status',
            width: 100,
            render: (_, n) => n.missing ? <Tag color="error">不存在</Tag>
                : n.status === 'active' ? <Tag color="success">活跃</Tag>
                : <Tag color="warning">{n.status || '-'}</Tag>
        }
    ];

    const renderConditions = (record) => {
        const c = [];
        if (record.cidr_id > 0) c.push(<Tag color="red" key="cidr">CIDR: {record.cidr_name || `#${record.cidr_id}`}</Tag>);
        if (record.city && record.city !== '0') c.push(<Tag color="blue" key="city">城市: {record.city_name || record.city}</Tag>);
        if (record.province && record.province !== '0') c.push(<Tag color="cyan" key="province">省: {record.province_name || record.province}</Tag>);
        if (record.country && record.country !== '0') c.push(<Tag color="green" key="country">国家: <CountryFlag code={record.country_name} style={{ marginInlineStart: 2 }} />{record.country_name || record.country}</Tag>);
        if (record.continent && record.continent !== '0') c.push(<Tag color="orange" key="continent">洲: {record.continent_name || record.continent}</Tag>);
        if (record.isp && record.isp !== '0') c.push(<Tag color="purple" key="isp">ISP: {record.isp_name || record.isp}</Tag>);
        if (!c.length) c.push(<Tag key="default">默认（不限条件）</Tag>);
        return <Space wrap size={4}>{c}</Space>;
    };

    const renderLinePanel = (line) => {
        const lineNodes = nodeIdsOf(line).map(id => nodeById[id] ? { ...nodeById[id] } : { id, missing: true });
        return (
            <div>
                <Space wrap style={{ width: '100%', justifyContent: 'space-between', marginBottom: 12 }}>
                    <Space wrap size={16}>
                        <span>TTL <Text strong>{line.ttl}</Text></span>
                        <span>优先级 <Text strong>{line.priority}</Text></span>
                        <span>权重 <Text strong>{line.weight}</Text></span>
                        {line.status === 'active' ? <Tag color="success">启用</Tag> : <Tag>停用</Tag>}
                        {renderConditions(line)}
                        {line.comment && <Tooltip title={line.comment}><Text type="secondary">备注</Text></Tooltip>}
                    </Space>
                    <Space>
                        <Button icon={<EditOutlined />} onClick={() => handleEdit(line)}>编辑</Button>
                        <Button danger icon={<DeleteOutlined />} onClick={() => handleDelete(line)} />
                    </Space>
                </Space>
                <Table
                    size="small"
                    columns={nodeColumns}
                    dataSource={lineNodes}
                    rowKey="id"
                    pagination={false}
                    scroll={{ x: 'max-content' }}
                    locale={{ emptyText: <Empty description="无节点" /> }}
                />
            </div>
        );
    };

    const newLineButton = (
        <Dropdown.Button
            type="primary"
            disabled={!selectedGroupId}
            onClick={() => handleCopy(activeLine)}
            menu={{
                items: [
                    { key: 'default', label: '复制默认线路', disabled: !defaultLine },
                    { key: 'blank', label: '空白线路' }
                ],
                onClick: ({ key }) => (key === 'default' ? handleCopy(defaultLine) : handleAdd())
            }}
        >
            <Tooltip title={activeLine ? `复制「${tabLabel(activeLine)}」的配置` : undefined}>
                <PlusOutlined /> 新建
            </Tooltip>
        </Dropdown.Button>
    );

    const modalTitle = editingRecord
        ? `编辑线路「${tabLabel(editingRecord)}」`
        : copySource ? `新建线路（复制自「${tabLabel(copySource)}」）` : '新建线路';

    return (
        <div>
            <Card
                title={
                    <Space>
                        <BranchesOutlined />
                        CDN线路记录管理
                    </Space>
                }
                extra={
                    <Space>
                        <Select
                            style={{ width: 200 }}
                            placeholder="选择CDN分组"
                            value={selectedGroupId}
                            onChange={(v) => { setActiveLineKey(null); setSelectedGroupId(v); }}
                        >
                            {groups.map(group => (
                                <Option key={group.id} value={group.id}>
                                    {group.name}
                                </Option>
                            ))}
                        </Select>
                        <Tooltip title="只读预览端点 DNS 变更">
                            <Button icon={<EyeOutlined />} onClick={handlePreview} loading={previewing} disabled={!selectedGroupId}>
                                预览变更
                            </Button>
                        </Tooltip>
                        <Button icon={<ReloadOutlined />} onClick={loadRecords} />
                    </Space>
                }
            >
                {unassignedNodes.length > 0 && (
                    <Alert
                        style={{ marginBottom: 12 }}
                        type="warning"
                        showIcon
                        message={<>未分配到任何线路的活跃节点：{unassignedNodes.map(n => <Tag key={n.id} color="orange">#{n.id} {n.name || n.hostname}</Tag>)}</>}
                    />
                )}

                {lines.length === 0 ? (
                    <Empty description={loading ? '加载中' : '该分组还没有线路'}>
                        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd} disabled={!selectedGroupId}>新建线路</Button>
                    </Empty>
                ) : (
                    <Tabs
                        activeKey={activeLine ? String(activeLine.id) : undefined}
                        onChange={setActiveLineKey}
                        tabBarExtraContent={newLineButton}
                        items={lines.map(l => ({
                            key: String(l.id),
                            label: (
                                <Space size={6}>
                                    <span style={l.status === 'active' ? undefined : { opacity: 0.5 }}>{tabLabel(l)}</span>
                                    <Tooltip title="节点数">
                                        <Badge count={nodeIdsOf(l).length} showZero size="small" color="#8c8c8c" />
                                    </Tooltip>
                                </Space>
                            ),
                            children: renderLinePanel(l)
                        }))}
                    />
                )}
            </Card>

            <Modal
                title={modalTitle}
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                onOk={handleSubmit}
                confirmLoading={saving}
                width={800}
                okText="保存并同步"
                cancelText="取消"
            >
                <Form form={form} layout="vertical">
                    <Form.Item name="group_id" hidden>
                        <Input />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="线路名称"
                                name="name"
                                rules={[
                                    { required: true, whitespace: true, message: '请输入线路名称' },
                                    { max: 64 },
                                    {
                                        validator: (_, v) => {
                                            const n = (v || '').trim();
                                            const taken = n && records.some(r => r.name === n && r.id !== editingRecord?.id);
                                            return taken ? Promise.reject(new Error('同组内名称已存在')) : Promise.resolve();
                                        }
                                    }
                                ]}
                            >
                                <Input placeholder="如 电信 / 联通 / 海外" autoFocus />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="状态" name="status" rules={[{ required: true }]}>
                                <Select>
                                    <Option value="active">启用</Option>
                                    <Option value="inactive">停用</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        label="节点"
                        name="node_ids"
                        rules={[{ required: true, message: '请选择至少一个节点' }]}
                    >
                        <Select
                            mode="multiple"
                            placeholder="选择节点"
                            showSearch
                            optionFilterProp="label"
                        >
                            {nodes.map(node => {
                                const isActive = node.status === 'active';
                                return (
                                    <Option
                                        key={node.id}
                                        value={node.id}
                                        disabled={!isActive}
                                        label={`${nodeLabel(node)} (${node.ip})`}
                                    >
                                        <Space size={4}>
                                            <span>{nodeLabel(node)} ({node.ip})</span>
                                            {!isActive && (
                                                <Tag color="error" icon={<WarningOutlined />} style={{ marginInlineEnd: 0 }}>
                                                    {node.status === 'inactive' ? '未激活' : (node.status || '死节点')}
                                                </Tag>
                                            )}
                                        </Space>
                                    </Option>
                                );
                            })}
                        </Select>
                    </Form.Item>

                    <Form.Item label={t('IP pools')} name="pool_ids">
                        <Select
                            mode="multiple"
                            allowClear
                            optionFilterProp="label"
                            options={ipPools.map((p) => ({ value: p.id, label: p.status === 'active' ? p.name : `${p.name} (${t('Disabled')})` }))}
                        />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item label="TTL (秒)" name="ttl" rules={[{ required: true }]}>
                                <InputNumber min={60} max={86400} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item label="优先级" name="priority" tooltip="数值越小优先级越高">
                                <InputNumber min={0} max={255} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item label="权重" name="weight" tooltip="同优先级下的负载均衡权重">
                                <InputNumber min={0} max={255} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Collapse
                        ghost
                        items={[{
                            key: 'adv',
                            label: '线路条件 / 备注',
                            forceRender: true,
                            children: (
                    <Tabs defaultActiveKey="geo" items={[
                        { key: 'geo', label: '地理位置', forceRender: true, children: (<>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item label="大洲" name="continent">
                                        <Select placeholder="不限制" allowClear showSearch onChange={handleContinentChange}>
                                            <Option value="0">不限制</Option>
                                            {continents.map(c => (
                                                <Option key={c.id} value={c.id.toString()}>
                                                    {c.continent || c.name}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item label="国家" name="country">
                                        <Select placeholder="不限制" allowClear showSearch optionFilterProp="search" onChange={handleCountryChange}>
                                            <Option value="0" search="不限制">不限制</Option>
                                            {countries.map(c => (
                                                <Option key={c.id} value={c.id.toString()} search={`${c.country_code} ${c.country_name}`}>
                                                    <CountryFlag code={c.country_code} />{c.country_code} - {c.country_name}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item label="省份/州" name="province">
                                        <Select placeholder="不限制" allowClear showSearch onChange={handleProvinceChange}>
                                            <Option value="0">不限制</Option>
                                            {regions.map(r => (
                                                <Option key={r.id} value={r.id.toString()}>
                                                    {r.region_name}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item label="城市" name="city">
                                        <Select placeholder="不限制" allowClear showSearch>
                                            <Option value="0">不限制</Option>
                                            {cities.map(c => (
                                                <Option key={c.id} value={c.id.toString()}>
                                                    {c.city_name}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                            </Row>
                        </>) },
                        { key: 'isp', label: 'ISP', forceRender: true, children: (
                            <Form.Item noStyle shouldUpdate={(prev, curr) => prev.country !== curr.country}>
                                {({ getFieldValue }) => {
                                    const geoCountry = getFieldValue('country');
                                    const hasGeoCountry = geoCountry && geoCountry !== '0';
                                    return (
                                        <Row gutter={16}>
                                            <Col span={8}>
                                                <Form.Item
                                                    label="ISP国家"
                                                    name="isp_country"
                                                    tooltip={hasGeoCountry ? '已同步地理位置国家' : undefined}
                                                >
                                                    <Select
                                                        placeholder="不限制"
                                                        allowClear={!hasGeoCountry}
                                                        showSearch
                                                        optionFilterProp="search"
                                                        disabled={hasGeoCountry}
                                                        onChange={handleIspCountryChange}
                                                    >
                                                        <Option value="" search="不限制">不限制</Option>
                                                        {ispCountries.map(c => (
                                                            <Option key={c.country} value={c.country} search={`${c.country} ${c.country_name}`}>
                                                                <CountryFlag code={c.country} />{c.country_name}
                                                            </Option>
                                                        ))}
                                                    </Select>
                                                </Form.Item>
                                            </Col>
                                            <Col span={8}>
                                                <Form.Item label="ISP类型" name="isp_type">
                                                    <Select placeholder="不限制" allowClear showSearch onChange={handleIspTypeChange}>
                                                        <Option value="">不限制</Option>
                                                        {ispTypes.map((type, index) => (
                                                            <Option key={`isp-type-${index}`} value={type.code || type}>
                                                                {type.name || type}
                                                            </Option>
                                                        ))}
                                                    </Select>
                                                </Form.Item>
                                            </Col>
                                            <Col span={8}>
                                                <Form.Item label="ISP" name="isp">
                                                    <Select placeholder="不限制" allowClear showSearch>
                                                        <Option value="0">不限制</Option>
                                                        {isps.map(isp => (
                                                            <Option key={isp.id} value={isp.id.toString()}>
                                                                {isp.name}
                                                            </Option>
                                                        ))}
                                                    </Select>
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                    );
                                }}
                            </Form.Item>
                        ) },
                        { key: 'cidr', label: 'CIDR', forceRender: true, children: (
                            <Form.Item label="CIDR规则" name="cidr_id" tooltip="精确IP段匹配，优先级最高">
                                <Select placeholder="不使用CIDR" allowClear showSearch>
                                    <Option value={0}>不使用CIDR</Option>
                                    {cidrs.map(cidr => (
                                        <Option key={cidr.id} value={cidr.id}>
                                            {cidr.name} - {cidr.description || ''}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        ) },
                        { key: 'comment', label: '备注', forceRender: true, children: (
                            <Form.Item name="comment">
                                <TextArea rows={2} placeholder="可选" />
                            </Form.Item>
                        ) }
                    ]} />
                            )
                        }]}
                    />
                </Form>
            </Modal>

            <Modal
                title="预览变更"
                open={!!plan}
                onCancel={() => setPlan(null)}
                width={760}
                footer={[
                    <Button key="close" onClick={() => setPlan(null)}>关闭</Button>,
                    <Popconfirm
                        key="apply"
                        title="按此预览立即应用？"
                        onConfirm={handleSyncDns}
                        disabled={!plan?.has_changes}
                    >
                        <Button type="primary" icon={<SyncOutlined />} loading={syncLoading} disabled={!plan?.has_changes}>
                            应用
                        </Button>
                    </Popconfirm>
                ]}
            >
                <DnsSyncPlanView plan={plan} />
            </Modal>
        </div>
    );
};

export default CdnLineRecords;
